// ============================================================
// FotoUsuarioField — Subir / eliminar foto de perfil de un usuario.
// Se integra en el formulario de edición (no en creación).
// ============================================================

import React, { useEffect, useRef, useState } from 'react';
import { I, UserAvatar } from 'components/ui';
import { usuarioService } from 'services/usuarioService';
import {
  usuarioFotoService,
  validarArchivoFoto,
  TIPOS_FOTO_PERMITIDOS,
  TAMANIO_FOTO_MAX_MB_DEFAULT,
} from 'services/usuarioFotoService';
import { invalidarFotoUsuario } from 'hooks/useFotoUsuario';

interface Props {
  idUsuario: number;
  nombres:   string;
  apellidos: string;
}

const Spinner: React.FC<{ size?: number }> = ({ size = 4 }) => (
  <svg className={`animate-spin w-${size} h-${size}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

const FotoUsuarioField: React.FC<Props> = ({ idUsuario, nombres, apellidos }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fotoPerfil, setFotoPerfil] = useState<string | null>(null);
  const [maxMB, setMaxMB] = useState<number>(TAMANIO_FOTO_MAX_MB_DEFAULT);
  const [submitting, setSubmitting] = useState<'sube' | 'borra' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Cargar foto actual y tamaño máximo en paralelo
  useEffect(() => {
    let cancelled = false;
    usuarioService.obtener(idUsuario)
      .then(u => { if (!cancelled) setFotoPerfil(u.fotoPerfil); })
      .catch(() => { /* mantener null */ });
    usuarioFotoService.getTamanioMaxMB()
      .then(mb => { if (!cancelled) setMaxMB(mb); });
    return () => { cancelled = true; };
  }, [idUsuario]);

  const handleSeleccionar = () => inputRef.current?.click();

  const handleArchivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-seleccionar el mismo archivo
    if (!file) return;
    const err = validarArchivoFoto(file, maxMB);
    if (err) { setError(err); return; }
    setSubmitting('sube');
    setError(null);
    try {
      const updated = await usuarioFotoService.subir(idUsuario, file);
      setFotoPerfil(updated.fotoPerfil);
      invalidarFotoUsuario(idUsuario);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al subir la foto');
    } finally {
      setSubmitting(null);
    }
  };

  const handleEliminar = async () => {
    if (!fotoPerfil) return;
    setSubmitting('borra');
    setError(null);
    try {
      const updated = await usuarioFotoService.eliminar(idUsuario);
      setFotoPerfil(updated.fotoPerfil);
      invalidarFotoUsuario(idUsuario);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al eliminar la foto');
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50/40">
      <div className="flex items-center gap-4">
        <UserAvatar
          idUsuario={idUsuario}
          fotoPerfil={fotoPerfil}
          nombres={nombres}
          apellidos={apellidos}
          size={80}
          rounded="xl"
        />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Foto de perfil</p>
          <p className="text-xs text-slate-400 mt-0.5">
            JPG, PNG o WebP · Máximo {maxMB} MB
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              onClick={handleSeleccionar}
              disabled={submitting !== null}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold disabled:opacity-60"
            >
              {submitting === 'sube' ? <Spinner /> : <I.Upload size={13} />}
              {fotoPerfil ? 'Cambiar foto' : 'Subir foto'}
            </button>
            {fotoPerfil && (
              <button
                type="button"
                onClick={handleEliminar}
                disabled={submitting !== null}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 text-xs font-bold disabled:opacity-60"
              >
                {submitting === 'borra' ? <Spinner /> : <I.Trash size={13} />}
                Eliminar
              </button>
            )}
          </div>
          {error && (
            <p className="text-xs text-red-600 mt-2">{error}</p>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={TIPOS_FOTO_PERMITIDOS.join(',')}
        className="hidden"
        onChange={handleArchivo}
      />
    </div>
  );
};

export default FotoUsuarioField;
